import React from 'react';
import styled from 'styled-components';
import {
  colors,
  fonts,
  breakpoints,
  radii,
  headingStyle,
  sectionPaddingStyle,
  containerStyle,
  icones,
} from '../annuaire/designSystem';
import { fixerLiteWeeklyHours } from '../../annuaire/bookingState';
import {
  adresseMasquee,
  lieuApproximatif,
} from '../../annuaire/adressePublique';

const Section = styled.section`
  margin: 0;
  ${sectionPaddingStyle}
  background-color: ${colors.white};
`;

const Container = styled.div`
  ${containerStyle}
`;

const Heading = styled.h2`
  ${headingStyle}
  font-size: 1.5rem;
  margin-bottom: 32px;

  @media (min-width: ${breakpoints.mobile}) {
    font-size: 2.125rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr 1fr;
    gap: 56px;
  }
`;

const SubHeading = styled.h3`
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 1.2rem;
  margin-bottom: 20px;
  color: ${colors.textDark};
`;

// ---- Schedule Table ----

const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: ${radii.small};
  overflow: hidden;
  border: 1px solid ${colors.borderLight};
  font-family: ${fonts.body};
`;

const TableHead = styled.thead`
  th {
    background-color: ${colors.bleuFonce};
    color: ${colors.white};
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 0.9rem;
  }

  th:first-child {
    border-radius: 12px 0 0 0;
  }

  th:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const TableRow = styled.tr`
  ${(p) =>
    p.$today &&
    `
    background-color: ${colors.vertPastel};
    td {
      font-weight: 600;
      color: ${colors.textDark};
    }
  `}

  td {
    padding: 12px;
    border-bottom: 1px solid ${colors.borderLight};
    font-size: 0.95rem;
    color: ${colors.textDark};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

// ---- Contact List ----

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContactItem = styled.li`
  display: flex;
  gap: 20px;
  margin-bottom: 28px;
  align-items: flex-start;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  width: 56px;
  height: 56px;
  background-color: ${(p) => p.$bg || colors.bleuPastel};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
`;

const ContactContent = styled.div`
  h4 {
    font-family: ${fonts.body};
    font-weight: 600;
    font-size: 1rem;
    margin: 0 0 4px 0;
    padding: 0;
    color: ${colors.textDark};
    line-height: 1.2;
  }

  p {
    margin: 0;
    font-family: ${fonts.body};
    font-size: 1rem;
    color: ${colors.textSecondary};
    word-break: break-word;
  }

  a {
    color: ${colors.bleu};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      color: ${colors.textDark};
    }
  }
`;

const EditLink = styled.a`
  display: inline-block;
  margin-top: 20px;
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 1rem;
  color: ${colors.bleu};
  text-decoration: none;

  &:hover {
    color: ${colors.textDark};
  }
`;

// ---- Helpers ----

const DAYS_FR = [
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
  'Dimanche',
];

const DAYS_INDEX_MAP = {
  lundi: 1,
  mardi: 2,
  mercredi: 3,
  jeudi: 4,
  vendredi: 5,
  samedi: 6,
  dimanche: 0,
};

function getTodayFrench() {
  const jsDay = new Date().getDay(); // 0=Sunday
  const map = [
    'Dimanche',
    'Lundi',
    'Mardi',
    'Mercredi',
    'Jeudi',
    'Vendredi',
    'Samedi',
  ];
  return map[jsDay];
}

const DAYS_EN_TO_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche',
};

function translateDay(dayStr) {
  const lower = dayStr.toLowerCase();
  return (
    DAYS_EN_TO_FR[lower] || dayStr.charAt(0).toUpperCase() + dayStr.slice(1)
  );
}

function timeTo24h(h, m, period) {
  let hour = parseInt(h, 10);
  if (period === 'PM' && hour !== 12) hour += 12;
  if (period === 'AM' && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, '0')}:${m}`;
}

function convertRangeTo24h(rangeStr) {
  const parts = rangeStr.trim().split(/\s*[–\-—]\s*/);
  if (parts.length !== 2) return rangeStr;
  const timeRe = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i;
  const startMatch = parts[0].match(timeRe);
  const endMatch = parts[1].match(timeRe);
  if (!startMatch || !endMatch) return rangeStr;
  const endPeriod = endMatch[3] ? endMatch[3].toUpperCase() : null;
  const startPeriod = startMatch[3] ? startMatch[3].toUpperCase() : endPeriod;
  if (!endPeriod) return rangeStr;
  return `${timeTo24h(startMatch[1], startMatch[2], startPeriod)}–${timeTo24h(
    endMatch[1],
    endMatch[2],
    endPeriod,
  )}`;
}

function translateHours(text) {
  let result = text
    .replace(/\bClosed\b/gi, 'Fermé')
    .replace(/\bOpen 24 hours\b/gi, 'Ouvert 24h');
  if (/\d{1,2}:\d{2}\s*(AM|PM)/i.test(result)) {
    result = result.split(',').map(convertRangeTo24h).join(', ');
  }
  return result;
}

function parseHoraires(horaires) {
  if (!horaires) return [];

  // If weekdayDescriptions is available (Google format)
  if (
    horaires.weekdayDescriptions &&
    Array.isArray(horaires.weekdayDescriptions)
  ) {
    return horaires.weekdayDescriptions.map((desc) => {
      // Format: "Monday: 09:00–12:00, 14:00–19:00" or "Monday: Closed"
      const colonIndex = desc.indexOf(':');
      if (colonIndex === -1) return { day: translateDay(desc), hours: '' };
      const day = desc.substring(0, colonIndex).trim();
      const hours = desc.substring(colonIndex + 1).trim();
      return { day: translateDay(day), hours: translateHours(hours) };
    });
  }

  // If horaires_formatted is available
  if (
    horaires.horaires_formatted &&
    Array.isArray(horaires.horaires_formatted)
  ) {
    return horaires.horaires_formatted.map((item) => ({
      day: item.jour || item.day || '',
      hours: item.horaires || item.hours || '',
    }));
  }

  // Fallback: return default days with unknown hours
  return DAYS_FR.map((day) => ({ day, hours: 'Non renseigné' }));
}

// ---- Component ----

export default function ShopInfosPratiques({ shop }) {
  const details = shop.details || {};
  const horairesFormatted = details.horaires_formatted;
  let horaires = details.horaires || {};
  if (
    !horaires.weekdayDescriptions &&
    !horaires.horaires_formatted &&
    typeof horairesFormatted === 'string' &&
    horairesFormatted
  ) {
    horaires = {
      weekdayDescriptions: horairesFormatted.split('\n').filter(Boolean),
    };
  }
  const phone = shop.phone || details.telephone_google || '';
  const website = shop.website || details.site_web || '';
  const address = shop.address || '';
  const city = shop.city || '';
  const postalCode = shop.postal_code || '';
  // FIXERLIGHT-268 : le pro ne publie que son code postal et sa ville.
  const secteur = adresseMasquee(shop) ? lieuApproximatif(shop) : '';

  // FIXERLIGHT-068 : un atelier Fixer Lite a réglé ses horaires dans ses
  // Réglages (settings.opening_hours côté api), pas dans les horaires Google
  // — que ShopCreator::create() n'écrit jamais pour ce type d'atelier. Sans
  // ce repli, la fiche affichait « Non renseigné » sur les 7 jours malgré un
  // gabarit bien réglé, alors que le badge « Ouvert » (ShopHero, TrustBanner,
  // ShopMap), lui, ne se basait pas sur cette même absence.
  const scheduleRows = fixerLiteWeeklyHours(shop) || parseHoraires(horaires);
  const todayStr = getTodayFrench();

  return (
    <Section>
      <Container>
        <Heading>Infos pratiques</Heading>
        <Grid>
          {/* Schedule column */}
          <div>
            <SubHeading>Horaires</SubHeading>
            <ScheduleTable>
              <TableHead>
                <tr>
                  <th>Jour</th>
                  <th>Horaires</th>
                </tr>
              </TableHead>
              <tbody>
                {scheduleRows.map((row, i) => (
                  <TableRow
                    key={i}
                    $today={row.day.toLowerCase() === todayStr.toLowerCase()}>
                    <td>{row.day}</td>
                    <td>{row.hours}</td>
                  </TableRow>
                ))}
              </tbody>
            </ScheduleTable>
          </div>

          {/* Contact column */}
          <div>
            <SubHeading>Contact</SubHeading>
            <ContactList>
              {phone && (
                <ContactItem>
                  <ContactIcon>
                    <img
                      src={icones.smartphone}
                      alt="T&eacute;l&eacute;phone"
                    />
                  </ContactIcon>
                  <ContactContent>
                    <h4>T&eacute;l&eacute;phone</h4>
                    <p>
                      <a href={`tel:${phone}`}>{phone}</a>
                    </p>
                  </ContactContent>
                </ContactItem>
              )}
              {website && (
                <ContactItem>
                  <ContactIcon $bg={colors.orangePastel}>
                    <img src={icones.infos} alt="Site web" />
                  </ContactIcon>
                  <ContactContent>
                    <h4>Site web</h4>
                    <p>
                      <a
                        href={website}
                        target="_blank"
                        rel="noopener noreferrer">
                        {website}
                      </a>
                    </p>
                  </ContactContent>
                </ContactItem>
              )}
              {secteur && (
                <ContactItem>
                  <ContactIcon $bg={colors.violetPastel}>
                    <img src={icones.localisation} alt="Secteur" />
                  </ContactIcon>
                  <ContactContent>
                    <h4>Secteur</h4>
                    <p>{secteur}</p>
                  </ContactContent>
                </ContactItem>
              )}
              {address && (
                <ContactItem>
                  <ContactIcon $bg={colors.violetPastel}>
                    <img src={icones.localisation} alt="Adresse" />
                  </ContactIcon>
                  <ContactContent>
                    <h4>Adresse</h4>
                    <p>
                      {address}
                      {postalCode || city ? (
                        <>
                          <br />
                          {postalCode} {city}
                        </>
                      ) : null}
                    </p>
                  </ContactContent>
                </ContactItem>
              )}
            </ContactList>
            <EditLink
              href="https://feedback.roulezjeunesse.com/ajout-modif-annuaire"
              target="_blank"
              rel="noopener noreferrer">
              Modifier les informations
            </EditLink>
          </div>
        </Grid>
      </Container>
    </Section>
  );
}
